import { useEffect, useState } from 'react';

import {
  BOTTOM_NAV_HEIGHT,
  shouldShowBuyerBottomNav,
} from '../layouts/bottom-nav/bottom-nav-config';

const PRODUCT_DETAIL_MOBILE_ACTION_HEIGHT = 96;

export type VisualViewportMetrics = {
  height: number;
  offsetTop: number;
};

export type ViewportMetrics = {
  innerHeight: number;
  visualViewport: VisualViewportMetrics | null;
};

function readWindowViewport(): ViewportMetrics {
  if (typeof window === 'undefined') {
    return { innerHeight: 0, visualViewport: null };
  }

  const visualViewport = window.visualViewport;
  if (!visualViewport) {
    return { innerHeight: window.innerHeight, visualViewport: null };
  }

  return {
    innerHeight: window.innerHeight,
    visualViewport: {
      height: visualViewport.height,
      offsetTop: visualViewport.offsetTop,
    },
  };
}

export function getVisualViewportInsetPx(
  viewport: ViewportMetrics = readWindowViewport()
): number {
  const visualViewport = viewport.visualViewport;
  if (!visualViewport) return 0;
  return Math.max(
    0,
    viewport.innerHeight - visualViewport.height - visualViewport.offsetTop
  );
}

export function getPageChromePx(pathname: string): number {
  if (shouldShowBuyerBottomNav(pathname)) {
    return BOTTOM_NAV_HEIGHT;
  }

  if (pathname.startsWith('/product/')) {
    return PRODUCT_DETAIL_MOBILE_ACTION_HEIGHT;
  }

  return 0;
}

export function getBannerBottomPx(
  pathname: string,
  visualInsetPx: number
): number {
  return getPageChromePx(pathname) + visualInsetPx;
}

export function useVisualViewportInset(): number {
  const [inset, setInset] = useState(() =>
    typeof window === 'undefined' ? 0 : getVisualViewportInsetPx()
  );

  useEffect(() => {
    const update = () => setInset(getVisualViewportInsetPx());
    update();
    const visualViewport = window.visualViewport;
    window.addEventListener('resize', update);
    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
    };
  }, []);

  return inset;
}
